/**
 * Pagination - Numeric pagination component
 * 
 * Displays page numbers with navigation controls.
 * Supports both controlled and callback-based navigation.
 */
"use client";

import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// ============================================================================
// Types
// ============================================================================

export interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Whether pagination is disabled (e.g., during loading) */
  disabled?: boolean;
  /** Maximum number of page buttons to show (default: 5) */
  maxVisible?: number;
  /** Additional CSS classes */
  className?: string;
  /** Show first/last page buttons */
  showFirstLast?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate which page numbers to display
 */
function getVisiblePages(current: number, total: number, maxVisible: number): number[] {
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + maxVisible - 1);

  // Adjust start if end is at the limit
  if (end === total) {
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

// ============================================================================
// Component
// ============================================================================

/**
 * Numeric pagination component
 * 
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={page}
 *   totalPages={10}
 *   onPageChange={setPage}
 * />
 * ```
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  maxVisible = 5,
  className,
  showFirstLast = false
}: PaginationProps) {
  // Don't render if only one page
  if (totalPages <= 1) {
    return null;
  }

  const visiblePages = getVisiblePages(currentPage, totalPages, maxVisible);
  const showLeftEllipsis = visiblePages[0] > 2;
  const showRightEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;

  const handlePageClick = (page: number) => {
    if (!disabled && page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
      // Scroll to top of gallery smoothly
      document.getElementById("artes")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const buttonBaseClass = clsx(
    "flex items-center justify-center rounded-full transition-all",
    "min-w-[36px] h-9 px-3 text-sm font-medium",
    disabled && "opacity-50 cursor-not-allowed"
  );

  const pageButtonClass = (isActive: boolean) => clsx(
    buttonBaseClass,
    isActive
      ? "bg-brand-secondary text-brand-dark shadow-[0_0_10px_rgba(34,197,94,0.3)]"
      : "border border-white/10 text-white/70 hover:bg-white/5 hover:text-white hover:border-white/20"
  );

  const navButtonClass = clsx(
    buttonBaseClass,
    "border border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
  );

  return (
    <nav
      aria-label="Paginação"
      className={clsx("flex items-center justify-center gap-1 sm:gap-2", className)}
    >
      {/* Previous button */}
      <button
        type="button"
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={disabled || currentPage === 1}
        className={clsx(navButtonClass, (disabled || currentPage === 1) && "opacity-30 cursor-not-allowed")}
        aria-label="Página anterior"
      >
        <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
      </button>

      {/* First page (if not visible) */}
      {showFirstLast && visiblePages[0] > 1 && (
        <>
          <button
            type="button"
            onClick={() => handlePageClick(1)}
            disabled={disabled}
            className={pageButtonClass(currentPage === 1)}
          >
            1
          </button>
          {showLeftEllipsis && (
            <span className="px-2 text-white/30">...</span>
          )}
        </>
      )}

      {/* Visible page numbers */}
      {visiblePages.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => handlePageClick(page)}
          disabled={disabled}
          className={pageButtonClass(currentPage === page)}
          aria-current={currentPage === page ? "page" : undefined}
        >
          {page}
        </button>
      ))}

      {/* Last page (if not visible) */}
      {showFirstLast && visiblePages[visiblePages.length - 1] < totalPages && (
        <>
          {showRightEllipsis && (
            <span className="px-2 text-white/30">...</span>
          )}
          <button
            type="button"
            onClick={() => handlePageClick(totalPages)}
            disabled={disabled}
            className={pageButtonClass(currentPage === totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* Next button */}
      <button
        type="button"
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={disabled || currentPage === totalPages}
        className={clsx(navButtonClass, (disabled || currentPage === totalPages) && "opacity-30 cursor-not-allowed")}
        aria-label="Próxima página"
      >
        <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
      </button>
    </nav>
  );
}

/**
 * Simple pagination info display
 */
export function PaginationInfo({
  currentPage,
  totalPages,
  className
}: {
  currentPage: number;
  totalPages: number;
  className?: string;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className={clsx("text-center text-xs text-white/40", className)}>
      Página {currentPage} de {totalPages}
    </div>
  );
}

export default Pagination;
